/**
 * useWorkLinks — o agrupamento EXPLÍCITO de trabalhos (`work_links`).
 *
 * A trilha continua sendo DERIVADA da corrente de `quote_id`; esta camada só
 * existe para os dois casos que a derivação não expressa, porque a corrente tem
 * raiz única: dois orçamentos para o mesmo serviço (o cliente recusou a v1) e um
 * contrato avulso, que não alcança a OS. Ver o cabeçalho da migration.
 *
 * **Vincular é ONLINE-ONLY**, pela mesma razão das variações (#30c), do kit
 * (#65) e da despesa cobrável (#76): a fila offline mapeia UMA operação para UM
 * uuid, então não há como resolver o par de documentos de um vínculo criado sem
 * rede. A UI recusa com aviso em vez de enfileirar algo que não sincroniza.
 *
 * A LEITURA, ao contrário, entra no pool normal do trabalho e funciona offline
 * quando os vínculos já vieram — agrupar é configuração, ler é dado de negócio.
 */
import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import type { WorkRoot, WorkRootKind } from '@/lib/work';

const LINKS_KEY = 'work-links';

export interface WorkLink {
  id: string;
  work_key: string;
  document_kind: WorkRootKind;
  document_id: string;
}

/** Só documentos capazes de ser RAIZ podem ser agrupados (recibo é folha). */
export function isGroupableRoot(kind: WorkRootKind): kind is 'quote' | 'service_order' | 'contract' {
  return kind === 'quote' || kind === 'service_order' || kind === 'contract';
}

/**
 * Todas as raízes que compartilham o grupo de `root` — incluindo ela.
 *
 * Sem vínculo, devolve só a própria raiz: o comportamento derivado continua
 * sendo o padrão, e nenhum trabalho muda por a tabela existir.
 */
export function resolveWorkGroup(root: WorkRoot, links: WorkLink[]): WorkRoot[] {
  const own = links.find((l) => l.document_kind === root.kind && l.document_id === root.id);
  if (!own) return [root];
  const siblings = links
    .filter((l) => l.work_key === own.work_key)
    .map((l) => ({ kind: l.document_kind, id: l.document_id }));
  // A própria raiz vem primeiro: ela é a que o usuário está olhando, e é dela
  // que saem título e valor quando o grupo não tem outra pista.
  const rest = siblings.filter((s) => !(s.kind === root.kind && s.id === root.id));
  return [root, ...rest];
}

export function useWorkLinks() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const queryClient = useQueryClient();
  const companyId = activeCompany?.id ?? null;

  const query = useQuery({
    queryKey: [LINKS_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 60 * 1000,
    refetchOnMount: 'always',
    queryFn: async (): Promise<WorkLink[]> => {
      if (!companyId) return [];
      // `work_links` é nova e ainda não está em `integrations/supabase/types.ts`
      // (gerado). O cast segue o padrão já usado no repo para tabelas acessadas
      // dinamicamente; regenerar o arquivo inteiro arrastaria diffs alheios.
      const { data, error } = await (supabase as any)
        .from('work_links')
        .select('id, work_key, document_kind, document_id')
        .eq('company_id', companyId);
      if (error) throw error;
      return (data ?? []) as WorkLink[];
    },
  });

  const links = query.data ?? [];

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [LINKS_KEY, companyId] });
    queryClient.invalidateQueries({ queryKey: ['work'] });
    queryClient.invalidateQueries({ queryKey: ['works'] });
  }, [queryClient, companyId]);

  /**
   * Junta duas raízes no mesmo trabalho.
   *
   * Se qualquer uma já pertence a um grupo, o `work_key` existente vence — juntar
   * A (sozinha) com B (que já tem grupo) tem que colocar A no grupo de B, e não
   * criar um terceiro grupo deixando B para trás.
   */
  const group = useMutation({
    // Falha rápido sem rede em vez de pausar (o default 'online' pendura a
    // mutation e o aviso nunca aparece — #41).
    networkMode: 'offlineFirst',
    mutationFn: async ({ a, b }: { a: WorkRoot; b: WorkRoot }) => {
      if (!user?.id || !companyId) throw new Error('Sem empresa ativa.');
      const existing = links.find(
        (l) =>
          (l.document_kind === a.kind && l.document_id === a.id) ||
          (l.document_kind === b.kind && l.document_id === b.id),
      );
      const workKey = existing?.work_key ?? crypto.randomUUID();

      const rows = [a, b]
        .filter((doc) => !links.some((l) => l.document_kind === doc.kind && l.document_id === doc.id))
        .map((doc) => ({
          company_id: companyId,
          user_id: user.id,
          work_key: workKey,
          document_kind: doc.kind,
          document_id: doc.id,
        }));
      if (rows.length === 0) return;
      const { error } = await (supabase as any).from('work_links').insert(rows);
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  /** Tira UM documento do grupo. Os demais continuam juntos. */
  const ungroup = useMutation({
    networkMode: 'offlineFirst',
    mutationFn: async (root: WorkRoot) => {
      if (!companyId) throw new Error('Sem empresa ativa.');
      const own = links.find((l) => l.document_kind === root.kind && l.document_id === root.id);
      const { error } = await (supabase as any)
        .from('work_links')
        .delete()
        .eq('company_id', companyId)
        .eq('document_kind', root.kind)
        .eq('document_id', root.id);
      if (error) throw error;

      // Um grupo de UM é o mesmo que nenhum grupo: `resolveWorkGroup` já devolve
      // só a própria raiz. A linha restante seria inerte, mas fica acumulando —
      // e um "grupo" invisível de um membro é exatamente o tipo de estado que
      // confunde quem for depurar isto depois.
      if (own) {
        const remaining = links.filter(
          (l) =>
            l.work_key === own.work_key &&
            !(l.document_kind === root.kind && l.document_id === root.id),
        );
        if (remaining.length === 1) {
          await (supabase as any)
            .from('work_links')
            .delete()
            .eq('company_id', companyId)
            .eq('work_key', own.work_key);
        }
      }
    },
    onSuccess: invalidate,
  });

  return { links, isLoading: query.isLoading, group, ungroup };
}
